import type { CSSProperties } from "react"
import { ChevronRight } from "lucide-react"

import heroBg from "@/assets/img/landing/general-bg.webp"
import heroPalm from "@/assets/img/landing/hero-palm.webp"
import chipZodiac from "@/assets/svg/landing/chip-zodiac.svg"
import chipReports from "@/assets/svg/landing/chip-reports.svg"
import chipMoon from "@/assets/svg/landing/chip-moon.svg"
import chipTarot from "@/assets/svg/landing/chip-tarot.svg"
import { Button } from "@/components/ui/button"

import { REFUND_MAILTO } from "../../config/landing-content"
import { LandingLogo } from "../landing-logo"

interface IntroSectionProps {
  /** Primary CTA — into the quiz. */
  onAction: () => void
  /** Header "Sign in" — to the sign-in page. */
  onSignIn: () => void
}

interface HeroChip {
  icon: string
  label: string
  color: string
  style: CSSProperties
}

const HERO_CHIPS: HeroChip[] = [
  { icon: chipZodiac, label: "Daily horoscopes", color: "rgba(149,101,253,0.3)", style: { left: 37, top: 136 } },
  { icon: chipReports, label: "Deep Reports", color: "rgba(255,192,20,0.3)", style: { left: 232, top: 44 } },
  { icon: chipMoon, label: "Moon & Biorhythm", color: "rgba(255,83,127,0.3)", style: { left: 16, top: 288 } },
  { icon: chipTarot, label: "Tarot & Runes", color: "rgba(127,231,24,0.3)", style: { left: 250, top: 216 } },
]

export function IntroSection({ onAction, onSignIn }: IntroSectionProps) {
  return (
    <section data-slot="intro-section" className="relative flex flex-col gap-4">
      <img
        src={heroBg}
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-60 w-full object-cover"
      />

      <div className="relative z-10 flex flex-col gap-4">
        <header className="flex items-start justify-between p-4">
          <LandingLogo />
          <div className="flex items-center gap-1">
            <a
              href={REFUND_MAILTO}
              className="flex h-[34px] items-center justify-center rounded-full bg-main-purple/20 px-3 text-sm font-medium text-main-purple backdrop-blur-[8px]"
            >
              Ask for refund
            </a>
            <button
              type="button"
              onClick={onSignIn}
              className="flex h-[34px] items-center justify-center gap-1 rounded-full bg-gradient-to-b from-peach to-purple-accent px-3 text-sm font-medium text-white"
            >
              Sign in
              <ChevronRight className="size-3.5" />
            </button>
          </div>
        </header>

        <div className="flex flex-col gap-4 px-4">
          <div className="flex flex-col gap-2 text-center">
            <h1 className="font-display text-[28px] font-medium leading-tight text-purple-light">
              Understand Yourself Through Personalized Spiritual Insights
            </h1>
            <p className="text-base leading-snug text-purple-light/70">
              Answer a few questions and unlock personal guidance about love, timing, destiny,
              energy, and the hidden patterns shaping your path
            </p>
          </div>

          <Button variant="gradient" size="xl" onClick={onAction}>
            Get My Insights
          </Button>
        </div>
      </div>

      {/* Hero palm with floating category chips */}
      <div className="relative h-[342px] w-full overflow-hidden bg-dark-base">
        <img
          src={heroPalm}
          alt=""
          aria-hidden
          className="absolute left-1/2 top-[-22px] h-[364px] w-full -translate-x-1/2 object-cover"
        />
        {/* Blend the image's top edge into the page background (no hard seam) */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-dark-base to-transparent"
        />
        {HERO_CHIPS.map((chip) => (
          <div
            key={chip.label}
            className="absolute flex items-center gap-2 rounded-full py-2 pl-2 pr-3 shadow-[0px_8px_32px_0px_rgba(13,8,28,0.4)] backdrop-blur-[6px]"
            style={{ ...chip.style, backgroundColor: chip.color }}
          >
            <img src={chip.icon} alt="" className="size-6" />
            <span className="text-xs font-semibold uppercase text-white">
              {chip.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}
